<template>
  <div v-if="title || content || placeholder || video || items" class="product-block" :class="additionalClass">
    <div class="product-block__inner">
      <h3 v-if="title" class="product-block__title main-heading">
        <LockIcon :published-to="publishedTo" />
        {{ title }}
      </h3>
      <div v-if="content || placeholder || video" class="product-block__content" :class="{ 'product-block__content_opened': contentState }">
        <div class="product-block__col-holder" ref="productBlockColHolder" v-resize="getContentHeight">
          <div v-if="content" class="product-block__col">
            <product-content :content="content" />
          </div>
          <div v-if="placeholder || video" class="product-block__col">
            <product-media :placeholder="placeholder" :video="video" />
          </div>
        </div>
        <div v-if="isContentCut" class="product-block__content-more-link">
          <span @click="toggleContent">
            {{ contentState ? langJson[lang].seeLess : langJson[lang].seeMore }}
          </span>
        </div>
      </div>
      <product-items v-if="items" :items="items" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';

  interface ContentItem {
    external?: { [key: string]: any };
    [key: string]: any;
  }

  interface VideoItem {
    [key: string]: any;
  }

  interface PlaceholderItem {
    [key: string]: any;
  }

  interface BlockProductItem {
    id: string | number;
    external?: {
      title?: string;
      [key: string]: any;
    };
    internal?: {
      title?: string;
      [key: string]: any;
    };
    publishedTo?: number;
    resources?: any[];
    [key: string]: any;
  }

  interface NodeItem {
    [key: string]: any;
  }

  interface ImageItem {
    [key: string]: any;
  }

  const props = defineProps<{
    title?: string;
    additionalClass?: string;
    publishedTo?: number;
    content?: ContentItem;
    video?: VideoItem;
    placeholder?: PlaceholderItem;
    items?: BlockProductItem[];
    nodes?: NodeItem[];
    images?: ImageItem[];
  }>();

  const mainStore = useMainStore();
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);

  const contentHeight = ref<number>(0);
  const maxContentHeight = ref<number>(250);
  const contentState = ref<boolean>(false);
  const productBlockColHolder = ref<HTMLElement | null>(null);
  let mo: MutationObserver | null = null;

  const isContentCut = computed((): boolean => contentHeight.value > maxContentHeight.value);

  function getContentHeight(): void {
    const el = productBlockColHolder.value;
    if (el) {
      // Use scrollHeight to detect overflow beyond the visible clamp
      contentHeight.value = el.scrollHeight || el.clientHeight || 0;
    }
  }

  function toggleContent(): void {
    contentState.value = !contentState.value;
  }

  let ro: ResizeObserver | null = null;
  onMounted(async () => {
    await nextTick();
    getContentHeight();
    // Observe element size changes caused by images or async content
    if (import.meta.client && 'ResizeObserver' in window) {
      const el = productBlockColHolder.value;
      if (el) {
        ro = new ResizeObserver(() => getContentHeight());
        ro.observe(el);
      }
    }

    // Observe DOM mutations for dynamic content (e.g., v-html)
    if (import.meta.client) {
      const el = productBlockColHolder.value;
      if (el && 'MutationObserver' in window) {
        mo = new MutationObserver(() => Promise.resolve().then(() => getContentHeight()));
        mo.observe(el, { childList: true, subtree: true, characterData: true });
      }
    }
  });

  onBeforeUnmount(() => {
    if (ro) {
      ro.disconnect();
      ro = null;
    }
    if (mo) {
      mo.disconnect();
      mo = null;
    }
  });

  // Recalculate when content object changes deeply
  watch(
    () => props.content,
    async () => {
      await nextTick();
      getContentHeight();
    },
    { deep: true }
  );
</script>
